import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSortable } from 'react-sortablejs';

import AddBtn from '../../../../../components/Buttons/AddBtn';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import { frontendNotification } from '../../../../../components/Notification';
import ReactSortableItem from '../../../../../components/ReactSortableItem';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import SingleSelect from '../../../../../components/SingleSelect';
import { DeleteIcon } from '../../../../../icons';
import {
  billingUnitApi,
  paymentConditionApi,
  paymentMethodApi,
  productTabPriceCodesAPI,
  smartsellDbTableColumnsAPI,
  smartsellDbTablesAPI,
  SmartsellEnvApi,
} from '../../../../../services/requests';
import { BillingUnit } from '../../../../../types/apiResponse/BillingUnit';
import { FormField, NewFormField, NewFormFieldOption } from '../../../../../types/apiResponse/form';
import { PaymentCondition, PaymentMethod } from '../../../../../types/apiResponse/order';
import { SmartsellDbTable } from '../../../../../types/apiResponse/smartsellInfra';
import { TextInputOnChangeEvent } from '../../../../../types/events';
import { SmartsellEnv, SmartsellEnvObj } from '../../../../../types/smartsell';
import {
  getBooleanOptions,
  getOptionByValue,
  getOptionsFromFormFieldTypes,
  getSelectOptionByBoolean,
  SelectOptionSpecValue,
} from '../../../../../utils/getSelectOptions';
import Container from './FormFieldsModalStyle';

type Props = {
  isVisible: boolean;
  closeModal: () => void;
  saveActionBtn: (field: FormField | NewFormField) => void;
  selectedField: FormField | NewFormField | null;
};

const defaultFormFieldData: NewFormField = {
  description: '',
  type: getOptionsFromFormFieldTypes()[0].value,
  options: [],
  erpReferenceKey: null,
  is_required: false,
  min_size: null,
  max_size: null,
  orderNumber: null,
  is_visible: true,
  db_smartsell_env: 'SMARTSELL_MOBILE',
  db_table_name: null,
  db_column_table_name: null,
};

const defaultFullAddressOptions = [
  { description: 'cep' },
  { description: 'uf' },
  { description: 'cidade' },
  { description: 'bairro' },
  { description: 'logradouro' },
];

export const FormFieldsModal = ({ isVisible, closeModal, saveActionBtn, selectedField }: Props) => {
  const { t } = useTranslation();

  const [formFieldData, setFormFieldData] = useState<NewFormField>(
    selectedField ?? defaultFormFieldData,
  );
  const [moreDetail, setMoreDetail] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [newOptionSelect, setNewOptionSelect] = useState<SelectOptionSpecValue<string> | null>(
    null,
  );

  const typeHasOptions = () =>
    formFieldData.type === 'mi' ||
    formFieldData.type === 'me' ||
    formFieldData.type === 'tab_price' ||
    formFieldData.type === 'payment_condition' ||
    formFieldData.type === 'payment_method' ||
    formFieldData.type === 'billing_unit' ||
    formFieldData.type === 'full_address';

  const typeHasAsyncOptions = () =>
    formFieldData.type === 'billing_unit' ||
    formFieldData.type === 'payment_method' ||
    formFieldData.type === 'payment_condition' ||
    formFieldData.type === 'tab_price';

  const handleAddOptionBtn = () => {
    if (newOption.length <= 0) {
      return;
    }

    if (formFieldData.options.find((o) => o.description === newOption) === undefined) {
      setFormFieldData({
        ...formFieldData,
        options: [...formFieldData.options, { description: newOption }],
      });

      setNewOption('');
      setNewOptionSelect(null);
    }
  };

  const addSelectOptionBtn = () => {
    if (newOptionSelect === null) {
      return;
    }

    if (formFieldData.options.find((o) => o.description === newOptionSelect.value) === undefined) {
      setFormFieldData({
        ...formFieldData,
        options: [...formFieldData.options, { description: newOptionSelect.value }],
      });

      setNewOption('');
      setNewOptionSelect(null);
    }
  };

  const handleSaveBtn = () => {
    if (formFieldData.description.length <= 0) {
      frontendNotification({ message: t('descriptionIsRequired'), type: 'warning' });
      return;
    }

    if (typeHasOptions()) {
      if (formFieldData.options.length <= 0) {
        frontendNotification({ message: t('optionIsRequired'), type: 'warning' });
        return;
      }
    }

    saveActionBtn(formFieldData);
  };

  const isMinSizeInputVisible = () =>
    formFieldData.type === 'n' || formFieldData.type === 'currency_br';

  const isMaxSizeInputVisible = () =>
    formFieldData.type === 'n' ||
    formFieldData.type === 'currency_br' ||
    formFieldData.type === 't';

  const renderOptions = () => {
    if (formFieldData.options.length === 0) {
      return <div className="no-option">{t('noOption') as string}</div>;
    }

    const removeOption = (description: string) => {
      setFormFieldData({
        ...formFieldData,
        options: formFieldData.options.filter((o) => o.description !== description),
      });
    };

    const handleChangeList = (list: NewFormFieldOption[]) => {
      setFormFieldData({
        ...formFieldData,
        options: list,
      });
    };

    return (
      <ReactSortable
        list={formFieldData.options.map((item, index) => {
          return { ...item, id: index };
        })}
        setList={(s) => handleChangeList(s)}
        style={{ padding: '1rem' }}
      >
        {formFieldData.options.map((option) => (
          <ReactSortableItem key={option.description}>
            <div className="option-sortable-item">
              <label>{option.description}</label>

              <DeleteIcon
                title={t('delete')}
                onClick={() => removeOption(option.description)}
                size="1.5rem"
              />
            </div>
          </ReactSortableItem>
        ))}
      </ReactSortable>
    );
  };

  const disableAddOptionBtn = () => {
    if (
      formFieldData.type === 'tab_price' ||
      formFieldData.type === 'payment_condition' ||
      formFieldData.type === 'payment_method' ||
      formFieldData.type === 'billing_unit'
    ) {
      if (formFieldData.options.length > 0) {
        return true;
      }
    }

    return false;
  };

  const selectAsyncProps = {
    billing_unit: {
      request: billingUnitApi,
      reqResponseToOption: {
        mapper: {
          label: (item: BillingUnit) => `${item.CodigoUnidFat} - ${item.DescricaoUnidFat}`,
          value: (item: BillingUnit) => item.CodigoUnidFat,
        },
      },
      placeholder: t('billingUnit'),
      id: 'billingUnit',
    },
    payment_method: {
      request: paymentMethodApi,
      reqResponseToOption: {
        mapper: {
          label: (item: PaymentMethod) => item.Descricao,
          value: (item: PaymentMethod) => item.Codigo,
        },
      },
      placeholder: t('paymentMethod'),
      id: 'paymentMethod',
    },
    payment_condition: {
      request: paymentConditionApi,
      reqResponseToOption: {
        mapper: {
          label: (item: PaymentCondition) => item.Descricao,
          value: (item: PaymentCondition) => item.Codigo,
        },
      },
      placeholder: t('paymentCondition'),
      id: 'paymentCondition',
    },
    tab_price: {
      request: productTabPriceCodesAPI,
      reqResponseToOption: {
        mapper: {
          label: (item: string) => item,
          value: (item: string) => item,
        },
      },
      placeholder: t('tabPrice'),
      id: 'tabPrice',
    },
  };

  const handleSelectDbColumnQuery = () => {
    if (formFieldData.db_smartsell_env && formFieldData.db_table_name) {
      let query = `smartsellEnv=${formFieldData.db_smartsell_env}`;
      query += `&tableName=${formFieldData.db_table_name}`;
      return query;
    }
    return '';
  };

  return (
    <Modal
      title={t('addField')}
      showModal={isVisible}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={() => handleSaveBtn()}
      bodyStyle={formFieldData.options.length >= 2 || moreDetail ? { overflowX: 'scroll' } : {}}
    >
      <Container>
        <div className="form-row">
          <div className="itemFormWithLabel">
            <label htmlFor="field-description">{t('fieldDescription') as string}</label>
            <Input
              value={formFieldData.description}
              onChange={(e: TextInputOnChangeEvent) =>
                setFormFieldData({ ...formFieldData, description: e.target.value })
              }
              placeholder={t('field')}
              id="field-description"
            />
          </div>

          <div className="itemFormWithLabel">
            <label htmlFor="fieldType">{t('type') as string}</label>
            <SingleSelect
              value={getOptionByValue(formFieldData.type, getOptionsFromFormFieldTypes())}
              options={getOptionsFromFormFieldTypes()}
              onChange={(o) => {
                setFormFieldData({
                  ...formFieldData,
                  type: o.value,
                  options: o.value == 'full_address' ? defaultFullAddressOptions : [],
                });

                setNewOption('');
                setNewOptionSelect(null);
              }}
              placeholder={t('fieldType')}
              id="fieldType"
            />
          </div>

          <div className="itemFormWithLabel">
            <label htmlFor="erpReferenceKey">{t('erpReferenceKey') as string}</label>
            <input
              value={formFieldData.erpReferenceKey ?? ''}
              onChange={(e: TextInputOnChangeEvent) =>
                setFormFieldData({ ...formFieldData, erpReferenceKey: e.target.value })
              }
              placeholder={t('erpReferenceKey')}
              id="erpReferenceKey"
            />
          </div>
        </div>

        <div className="form-row">
          <div style={{ display: 'flex', marginLeft: '1rem', width: '100%' }}>
            <label
              htmlFor="moreDetail"
              style={{ display: 'flex', alignItems: 'center', margin: 0 }}
            >
              {t('moreDetail') as string}
            </label>
            <input
              type="checkbox"
              checked={moreDetail}
              onChange={() => setMoreDetail(!moreDetail)}
              id="moreDetail"
            />
            <div className="text-red" style={{ alignSelf: 'center' }}>
              {t('formSecondVersionInfo') as string}
            </div>
          </div>
        </div>

        {moreDetail && (
          <>
            <div className="form-row f-jc-start">
              <div className="itemFormWithLabel widthContent">
                <label htmlFor="isFieldRequired">{t('isRequired') as string}</label>
                <SingleSelect
                  value={getSelectOptionByBoolean(formFieldData.is_required)}
                  options={getBooleanOptions()}
                  onChange={(e) => setFormFieldData({ ...formFieldData, is_required: e.value })}
                  id="isFieldRequired"
                />
              </div>

              <div className="itemFormWithLabel widthContent">
                <label htmlFor="isFieldVisible">{t('isVisible') as string}</label>
                <SingleSelect
                  value={getSelectOptionByBoolean(formFieldData.is_visible)}
                  options={getBooleanOptions()}
                  onChange={(e) => setFormFieldData({ ...formFieldData, is_visible: e.value })}
                  id="isFieldVisible"
                />
              </div>

              {isMinSizeInputVisible() && (
                <div className="itemFormWithLabel widthContent">
                  <label htmlFor="fieldMinValue">{t('minValue') as string}</label>
                  <input
                    className="input"
                    type="number"
                    value={formFieldData.min_size ?? 0}
                    onChange={(e: any) =>
                      setFormFieldData({ ...formFieldData, min_size: e.target.value })
                    }
                    id="fieldMinValue"
                  />
                </div>
              )}

              {isMaxSizeInputVisible() && (
                <div className="itemFormWithLabel widthContent">
                  <label htmlFor="fieldSize">
                    {formFieldData.type === 't'
                      ? (t('maxSize') as string)
                      : (t('maxValue') as string)}
                  </label>
                  <input
                    className="input"
                    type="number"
                    value={formFieldData.max_size ?? 0}
                    onChange={(e: any) =>
                      setFormFieldData({ ...formFieldData, max_size: e.target.value })
                    }
                    id="fieldSize"
                  />
                </div>
              )}
            </div>

            <div className="form-row f-jc-start">
              <div className="itemFormWithLabel widthContent display-none">
                <label htmlFor="dbSmartsellEnv">{t('smartsellEnv') as string}</label>
                <SelectAsyncJSX
                  value={
                    formFieldData.db_smartsell_env
                      ? {
                        value: formFieldData.db_smartsell_env,
                        label: formFieldData.db_smartsell_env,
                      }
                      : null
                  }
                  onChange={(selected) =>
                    setFormFieldData({
                      ...formFieldData,
                      db_smartsell_env: selected?.value as SmartsellEnv,
                      db_table_name: null,
                      db_column_table_name: null,
                    })
                  }
                  request={SmartsellEnvApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: SmartsellEnvObj) => item.label,
                      value: (item: SmartsellEnvObj) => item.name,
                    },
                  }}
                  placeholder={t('dbEnv')}
                  id="dbSmartsellEnv"
                />
              </div>

              <div className="itemFormWithLabel widthContent">
                <label htmlFor="dbSmartsellTable">{t('table') as string}</label>
                <SelectAsyncJSX
                  key={`table-select-${formFieldData.db_smartsell_env || 'empty'}`}
                  value={
                    formFieldData.db_table_name
                      ? {
                        value: formFieldData.db_table_name,
                        label: formFieldData.db_table_name,
                      }
                      : null
                  }
                  onChange={(selected) =>
                    setFormFieldData({
                      ...formFieldData,
                      db_table_name: selected?.value,
                      db_column_table_name: null,
                    })
                  }
                  request={smartsellDbTablesAPI}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: SmartsellDbTable) => item.label,
                      value: (item: SmartsellDbTable) => item.name,
                    },
                  }}
                  placeholder={t('dbTable')}
                  id="dbSmartsellTable"
                  disabled={!formFieldData.db_smartsell_env}
                  query={
                    formFieldData.db_smartsell_env
                      ? `smartsellEnv=${formFieldData.db_smartsell_env}`
                      : ''
                  }
                />
              </div>

              <div className="itemFormWithLabel widthContent">
                <label htmlFor="dbSmartsellColumn">{t('column') as string}</label>
                <SelectAsyncJSX
                  key={`column-select-${formFieldData.db_table_name || 'empty'}`}
                  value={
                    formFieldData.db_column_table_name
                      ? {
                        value: formFieldData.db_column_table_name,
                        label: formFieldData.db_column_table_name,
                      }
                      : null
                  }
                  onChange={(selected) =>
                    setFormFieldData({
                      ...formFieldData,
                      db_column_table_name: selected?.value,
                    })
                  }
                  request={smartsellDbTableColumnsAPI}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: SmartsellDbTable) => item.label,
                      value: (item: SmartsellDbTable) => item.name,
                    },
                  }}
                  placeholder={t('dbTableColumn')}
                  id="dbSmartsellColumn"
                  disabled={!formFieldData.db_table_name}
                  cacheUniqs={[formFieldData.db_table_name]}
                  query={handleSelectDbColumnQuery()}
                />
              </div>
            </div>

            <div className="division ml-1r" />
          </>
        )}

        {typeHasAsyncOptions() && (
          <div className="itemFormWithLabel">
            <label htmlFor="paymentMethod">{t('paymentMethod')}</label>
            <div style={{ display: 'flex' }}>
              <SelectAsyncJSX
                key={formFieldData.type}
                value={newOptionSelect}
                onChange={setNewOptionSelect}
                request={
                  selectAsyncProps[formFieldData.type as keyof typeof selectAsyncProps].request
                }
                reqResponseToOption={
                  selectAsyncProps[formFieldData.type as keyof typeof selectAsyncProps]
                    .reqResponseToOption
                }
                placeholder={
                  selectAsyncProps[formFieldData.type as keyof typeof selectAsyncProps].placeholder
                }
                id={selectAsyncProps[formFieldData.type as keyof typeof selectAsyncProps].id}
              />

              <AddBtn
                style={{ marginLeft: '1rem' }}
                onClick={addSelectOptionBtn}
                disabled={disableAddOptionBtn()}
              />
            </div>
          </div>
        )}

        {typeHasOptions() && (
          <>
            <div className="division ml-1r" />
            <p className="labelSection ml-1r">{t('options') as string}</p>

            {(formFieldData.type === 'mi' ||
              formFieldData.type === 'me' ||
              formFieldData.type === 'full_address') && (
              <div>
                <div className="itemFormWithLabel">
                  <label htmlFor="newOption">{t('newOption')}</label>
                  <div style={{ display: 'flex' }}>
                    <input
                      id="newOption"
                      className="input"
                      value={newOption}
                      onChange={(e: TextInputOnChangeEvent) => setNewOption(e.target.value)}
                      placeholder={t('newOption')}
                    />
                    <AddBtn style={{ marginLeft: '1rem' }} onClick={handleAddOptionBtn} />
                  </div>
                </div>
              </div>
            )}

            {renderOptions()}
          </>
        )}
      </Container>
    </Modal>
  );
};

export default FormFieldsModal;
